import { useMemo } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { ko } from 'date-fns/locale/ko';
import { format, parseISO, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import { cn } from '@/lib/cn';

/* 한국어 로케일 1회 등록 — 모듈 평가 시점 */
registerLocale('ko', ko);

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * 사이트 공통 DatePicker — react-datepicker(9.x) 기반.
 *
 * 외부 API 는 기존 동일 (value 문자열 'YYYY-MM-DD', onChange 동일 포맷).
 * 사용처: WritePage 이벤트 폼, TermsFormPage 등.
 *
 * 사이트 컬러 토큰 매칭은 globals.css 의 `.sisegonggan-datepicker` 셀렉터에서 처리.
 */
export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = '날짜 선택',
  disabled,
  className,
  ariaLabel,
}: DatePickerProps) {
  const selected = useMemo(() => {
    if (!value) return null;
    const d = parseISO(value);
    return isValid(d) ? d : null;
  }, [value]);

  const minDate = useMemo(() => (min ? parseISO(min) : undefined), [min]);
  const maxDate = useMemo(() => (max ? parseISO(max) : undefined), [max]);

  /* 값 있으면 react-datepicker 의 isClearable X 버튼이 우측에 노출되므로
     자체 캘린더 아이콘은 숨김(겹침 방지). 값 없을 땐 placeholder 옆 아이콘. */
  const hasValue = !!selected;

  return (
    <div className={cn('relative inline-block w-full', className)}>
      <ReactDatePicker
        selected={selected}
        onChange={(d: Date | null) => onChange(d ? format(d, 'yyyy-MM-dd') : '')}
        locale="ko"
        dateFormat="yyyy.MM.dd (EEE)"
        minDate={minDate}
        maxDate={maxDate}
        placeholderText={placeholder}
        disabled={disabled}
        todayButton="오늘"
        isClearable
        showPopperArrow={false}
        ariaLabelledBy={ariaLabel}
        wrapperClassName="w-full"
        popperClassName="sisegonggan-datepicker-popper z-50"
        calendarClassName="sisegonggan-datepicker"
        className={cn(
          'h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 pr-10 text-sm',
          'text-[var(--color-text)] placeholder:text-[var(--color-text-sub)]',
          'transition-colors hover:border-[var(--color-primary)]/40',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/40',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      />
      {!hasValue && (
        <CalendarIcon
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-sub)]"
          aria-hidden
        />
      )}
    </div>
  );
}
